import { ReactNode, useCallback } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Sidebar } from "./Sidebar";
import { PWAStatus } from "@/components/pwa/PWAStatus";
import { WorldCupCountdownBar } from "@/components/world-cup/WorldCupCountdownBar";
import { useRealtimeSync } from "@/hooks/useRealtimeSync";
import { useRealtimeBroadcast } from "@/hooks/useRealtimeBroadcast";
import { usePageFocus } from "@/hooks/usePageFocus";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface AppLayoutProps {
  children?: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Detect SG7 pages to apply dark background to reserved padding area
  const isSG7Page = location.pathname.startsWith('/sg7-picks');
  
  // ✅ Realtime v2 (postgres_changes)
  useRealtimeSync();
  
  // 📡 Realtime Broadcast (Fase 3)
  useRealtimeBroadcast();

  // 🔄 Resume handler: refresh session and odds when app returns from background
  const handleAppResume = useCallback(async () => {
    console.log('📱 App resumed from background - refreshing session and data');
    
    try {
      // Refresh auth session if user is logged in
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase.auth.refreshSession();
        console.log('✅ Session refreshed');
      }
    } catch (error) {
      console.warn('⚠️ Session refresh failed (may be logged out):', error);
    }
    
    // Invalidate odds queries to refetch fresh data
    queryClient.invalidateQueries({ queryKey: ['odds-full'] });
    queryClient.invalidateQueries({ queryKey: ['odds'] });
    console.log('🔄 Odds queries invalidated');
  }, [queryClient]);

  // Trigger refresh after 30 seconds of inactivity
  usePageFocus({
    onFocus: handleAppResume,
    minInactiveTime: 30000, // 30 seconds
  });

  return (
    <div className={cn(
      "min-h-screen",
      isSG7Page ? "bg-[hsl(var(--sg7-surface-1))]" : "bg-background"
    )}>
      <div className="flex">
        <Sidebar />
        
        <main className={cn(
          "flex-1 min-w-0 ml-0 lg:ml-64 pb-20 lg:pb-24 transition-all duration-300 overflow-x-clip lg:max-w-[calc(100vw-16rem)] max-w-full relative",
          isSG7Page && "bg-[hsl(var(--sg7-surface-1))]"
        )}>
          <WorldCupCountdownBar />
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}